// Persistence for "peer manually verified in person" state.
//
// The safety number is computed deterministically from both peers' X25519
// public keys (see dmCrypto.computeSafetyNumber). When two users compare
// the number out-of-band (voice call, in person, QR scan…), they can mark
// each other as verified. This state is stored client-side only — the
// server never learns about it — and is scoped to the peer's identity id.
//
// Storage key: `ol.dm.verified.<identityId>` → "1"
import { readItem, writeItem, removeItem } from "./localStore";

const KEY_PREFIX = "ol.dm.verified.";

function keyFor(identityId) {
  return KEY_PREFIX + String(identityId || "").trim().toLowerCase();
}

export function isPeerVerified(identityId) {
  if (!identityId) return false;
  return readItem(keyFor(identityId)) === "1";
}

export function markPeerVerified(identityId) {
  if (!identityId) return false;
  writeItem(keyFor(identityId), "1");
  return true;
}

export function unmarkPeerVerified(identityId) {
  if (!identityId) return;
  removeItem(keyFor(identityId));
}

// --- QR payload -----------------------------------------------------------
// The QR encodes a compact, versioned JSON with the two X25519 public keys
// in canonical (sorted) order plus the safety number. The scanner
// recomputes the safety number locally and only trusts the QR if it
// matches — so a tampered QR image can never trick the client.
export const SAFETY_QR_VERSION = 1;

export function buildSafetyQrPayload({ myX25519Hex, peerX25519Hex, safetyNumber }) {
  const a = String(myX25519Hex || "").toLowerCase();
  const b = String(peerX25519Hex || "").toLowerCase();
  const [lo, hi] = a < b ? [a, b] : [b, a];
  return JSON.stringify({ v: SAFETY_QR_VERSION, t: "ol-dm-sn", sn: safetyNumber, a: lo, b: hi });
}

export function parseSafetyQrPayload(text) {
  if (!text || typeof text !== "string") return null;
  try {
    const data = JSON.parse(text);
    if (!data || data.t !== "ol-dm-sn" || data.v !== SAFETY_QR_VERSION) return null;
    if (typeof data.sn !== "string" || typeof data.a !== "string" || typeof data.b !== "string") return null;
    if (!/^[0-9a-f]{64}$/.test(data.a) || !/^[0-9a-f]{64}$/.test(data.b)) return null;
    return { safetyNumber: data.sn, a: data.a, b: data.b };
  } catch {
    return null;
  }
}
